import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2, Paperclip } from "lucide-react";
import { expensesApi } from "@/lib/billsNative";
import { useSession } from "@/contexts/SessionContext";

const money = (v, c = "INR") => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${c === "INR" ? "₹" : `${c} `}${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

function Row({ label, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-archival last:border-b-0">
      <dt className="text-[12px] text-archival-muted">{label}</dt>
      <dd className="text-[13px] text-archival-primary text-right" data-testid={testId}>{value ?? "—"}</dd>
    </div>
  );
}

function Tag({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-archival-soft text-archival-secondary",
    cash: "bg-emerald-50 text-emerald-700",
    stock: "bg-sky-50 text-sky-700",
    void: "bg-rose-50 text-rose-700",
  };
  return <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>{children}</span>;
}

export default function ExpenseDetail() {
  const { expenseId } = useParams();
  const { currentOutletId } = useSession();
  const [state, setState] = useState("loading");
  const [exp, setExp] = useState(null);

  const load = useCallback(async () => {
    setState("loading");
    const r = await expensesApi.get(expenseId);
    if (r.ok) {
      setExp(r.data);
      setState("ready");
    } else {
      setState(r.status === 404 ? "missing" : "error");
    }
  }, [expenseId]);

  useEffect(() => { if (currentOutletId) load(); }, [load, currentOutletId]);

  if (state === "loading") {
    return (
      <div className="py-24 text-center text-archival-muted" data-testid="expense-detail-loading">
        <Loader2 className="animate-spin mx-auto mb-2" size={18} /> Loading expense…
      </div>
    );
  }

  if (state !== "ready") {
    return (
      <div className="max-w-xl mx-auto py-16 text-center" data-testid="expense-detail-error">
        <AlertTriangle className="mx-auto mb-3 text-amber-600" size={22} />
        <h1 className="text-[18px] font-semibold text-archival-primary">
          {state === "missing" ? "Expense not found" : "Couldn't load this expense"}
        </h1>
        <p className="text-[13px] text-archival-secondary mt-1">
          {state === "missing"
            ? "It may belong to another outlet, or it may have been removed."
            : "Nothing has been changed. Try again in a moment."}
        </p>
        <div className="mt-4 flex items-center justify-center gap-2">
          <button onClick={load} className="rounded border border-archival px-3 py-1.5 text-[13px]"
            data-testid="expense-detail-retry">Retry</button>
          <Link to="/expenses" className="rounded border border-archival px-3 py-1.5 text-[13px]">
            Back to Expenses
          </Link>
        </div>
      </div>
    );
  }

  const cash = exp.cash_classification === "CASH" || exp.payment_method === "CASH";
  const stock = !!exp.impacts_inventory;
  const voided = exp.status === "VOID" || !!exp.void;
  const lines = exp.line_items || exp.stock_effect?.lines || [];
  const history = exp.audit || [];

  return (
    <div className="max-w-[1100px] mx-auto space-y-6" data-testid="expense-detail">
      <div>
        <Link to="/expenses" className="inline-flex items-center gap-1 text-[12px] text-archival-muted hover:text-archival-primary"
          data-testid="expense-back">
          <ArrowLeft size={13} /> Expenses
        </Link>
        <div className="mt-2 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h1 className="text-[26px] md:text-[28px] font-semibold text-archival-primary tracking-tight leading-tight">
              {money(exp.amount, exp.currency)}
            </h1>
            <p className="text-[13px] text-archival-secondary mt-1">
              {exp.description || exp.category || "Operating expense"} · {exp.business_date}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Tag tone={cash ? "cash" : "neutral"}>{cash ? "Cash" : "Non-cash"}</Tag>
            <Tag tone={stock ? "stock" : "neutral"}>{stock ? "Stock-impacting" : "Non-stock"}</Tag>
            {voided && <Tag tone="void">Voided</Tag>}
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <section className="rounded-md border border-archival bg-archival-surface p-4">
          <h2 className="text-[13px] font-semibold text-archival-primary mb-2">Expense</h2>
          <dl>
            <Row label="Amount" value={money(exp.amount, exp.currency)} testId="expense-amount" />
            <Row label="Date" value={exp.business_date} testId="expense-date" />
            <Row label="Category" value={exp.category} testId="expense-category" />
            <Row label="Type" value={exp.expense_type} />
            <Row label="Payment method" value={exp.payment_method} testId="expense-payment-mode" />
            <Row label="Outlet" value={exp.outlet_id} testId="expense-outlet" />
            <Row label="Vendor / payee" value={exp.payee_name || exp.payee_vendor_id} testId="expense-vendor" />
            <Row label="Status" value={exp.status} />
          </dl>
        </section>

        <section className="rounded-md border border-archival bg-archival-surface p-4">
          <h2 className="text-[13px] font-semibold text-archival-primary mb-2">Notes and evidence</h2>
          <dl>
            <Row label="Description" value={exp.description} />
            <Row label="Backdated reason" value={exp.backdated_reason} />
            <Row label="Recorded by" value={exp.created_by} />
            <Row label="Recorded at" value={exp.created_at ? String(exp.created_at).slice(0, 19).replace("T", " ") : null} />
          </dl>
          <div className="mt-3">
            {exp.source_evidence?.url || exp.source_evidence?.attachment_id ? (
              <a href={exp.source_evidence.url || "#"} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[12px] underline" data-testid="expense-evidence">
                <Paperclip size={12} /> Open source document
              </a>
            ) : (
              <p className="text-[12px] text-archival-muted" data-testid="expense-no-evidence">
                No source document was attached to this expense.
              </p>
            )}
          </div>
        </section>
      </div>

      {stock && (
        <section className="rounded-md border border-archival bg-archival-surface p-4" data-testid="expense-stock">
          <h2 className="text-[13px] font-semibold text-archival-primary mb-1">Stock effect</h2>
          <p className="text-[12px] text-archival-muted mb-3">
            Bills records what was bought. Inventory owns the resulting balances.
          </p>
          {lines.length === 0 ? (
            <p className="text-[12px] text-archival-muted">No canonical stock lines recorded.</p>
          ) : (
            <ul className="divide-y divide-archival">
              {lines.map((l, i) => (
                <li key={i} className="py-2 flex items-baseline justify-between gap-3 text-[13px]"
                  data-testid={`expense-stock-line-${i}`}>
                  <span className="text-archival-primary">{l.ingredient_name || l.ingredient_id || "—"}</span>
                  <span className="text-archival-secondary tabular-nums">
                    {l.base_quantity ?? l.quantity ?? "—"} {l.base_unit || l.unit || ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[12px] text-archival-muted mt-3">
            Inventory delivery: <span className="font-medium">{exp.inventory_sync_status || "NOT_APPLICABLE"}</span>
          </p>
        </section>
      )}

      <section className="rounded-md border border-archival bg-archival-surface p-4">
        <h2 className="text-[13px] font-semibold text-archival-primary mb-2">History</h2>
        {history.length === 0 ? (
          <p className="text-[12px] text-archival-muted">No recorded events.</p>
        ) : (
          <ol className="space-y-2">
            {history.map((h, i) => (
              <li key={i} className="text-[12px] text-archival-secondary" data-testid={`expense-history-${i}`}>
                <span className="font-medium text-archival-primary">{h.action}</span>
                {h.by ? ` · ${h.by}` : ""}
                {h.at ? ` · ${String(h.at).slice(0, 19).replace("T", " ")}` : ""}
                {h.reason ? ` · ${h.reason}` : ""}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
